// Contracts — work.weern.com PRINT ASSIGNMENTS (2026-06-23).
// The per-photographer asset registry of "what was sent to print to whom".
// Backs PRINTED capture mode: the worker accepts only a code assigned (and
// pending) to the signed-in photographer and matches it on the spot. Shapes
// snapped from migrations/0006_print_assignments.sql. Validation is CODE-ONLY;
// name/event/etc. are metadata for the one-name-per-background guard + cabinet.
import { z } from 'zod';

// Printed categories only — gift cards are freeform (no assignment).
export const WorkPrintCategorySchema = z.enum(['tickets', 'coupons']);
export type WorkPrintCategory = z.infer<typeof WorkPrintCategorySchema>;

// One asset in an ingest manifest. `email` keys the target photographer (must
// already be provisioned). `code` is the raw barcode/value as sent to print;
// the worker normalizes it to a code_key (codes.ts) for matching.
export const WorkPrintAssetInputSchema = z.object({
  email: z.string().trim().email().toLowerCase(),
  code: z.string().trim().min(1).max(4096),
  category: WorkPrintCategorySchema.default('tickets'),
  holder_name: z.string().trim().max(200).default(''),
  event: z.string().trim().max(200).default(''),
  location: z.string().trim().max(200).default(''),
  organizer: z.string().trim().max(200).default(''),
  // Платформа пачки (lepointdevente / tickeri / ticketplus / eventbrite…).
  // Единственный источник мерчанта для печатного кадра: при съёмке взять его
  // больше неоткуда, а каталог и QA-доска группируют по merchant×country.
  merchant: z.string().trim().max(120).default(''),
  country: z.string().trim().max(100).default(''),
  language: z.string().trim().max(50).default(''),
});
export type WorkPrintAssetInput = z.infer<typeof WorkPrintAssetInputSchema>;

// POST /api/work/_admin/printAssign — bulk upsert the print manifest
// (X-Admin-Secret). Receiving end of the assignment pipeline; the upstream
// producer (which decides who prints what) is separate.
export const WorkPrintAssignReqSchema = z.object({
  items: z.array(WorkPrintAssetInputSchema).min(1).max(5000),
});
export type WorkPrintAssignReq = z.infer<typeof WorkPrintAssignReqSchema>;

export const WorkPrintAssignResSchema = z.object({
  ok: z.boolean(),
  inserted: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  // Emails in the manifest that don't map to a provisioned photographer — those
  // items are skipped (provision first, then re-upload).
  skipped_no_photographer: z.array(z.string()).default([]),
  // Assignments whose photo already existed (photographer shot BEFORE the
  // manifest arrived) — retro-matched to captured on this ingest.
  reconciled: z.number().int().nonnegative().default(0),
});
export type WorkPrintAssignRes = z.infer<typeof WorkPrintAssignResSchema>;

// GET /api/work/assignments — the signed-in photographer's print queue: overall
// counts + groups (by holder + event) so the cabinet can show "what's left to
// shoot". No codes leak to the client (matching is server-side).
export const WorkAssignmentGroupSchema = z.object({
  holder_name: z.string(),   // '' = unnamed (coupons)
  event: z.string(),
  category: WorkPrintCategorySchema,
  total: z.number().int().nonnegative(),
  captured: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
});
export type WorkAssignmentGroup = z.infer<typeof WorkAssignmentGroupSchema>;

export const WorkAssignmentsResSchema = z.object({
  assigned: z.number().int().nonnegative(),
  captured: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
  groups: z.array(WorkAssignmentGroupSchema),
});
export type WorkAssignmentsRes = z.infer<typeof WorkAssignmentsResSchema>;
